/**
 * TagListModal — iPad Settings style tag manager.
 *
 * Pill cloud layout: tags as flowing rounded-md pills inside an inset container,
 * with an inline input at the end of the flow. Suggestion chips below.
 * Edit mode reveals X buttons on each pill (width/opacity transition).
 *
 * Follows the same modal skeleton as AttachmentListModal.
 */

import { useState, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tag as TagIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAddTag, useRemoveTag } from "../api/useDocTags";
import { useTagSuggestions } from "../api/useTagSuggestions";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface TagListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doctype: string;
  name: string;
  tags: string[];
}

export function TagListModal({
  open,
  onOpenChange,
  doctype,
  name,
  tags,
}: TagListModalProps) {
  const { language } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      tags: getUI("tags", "Tags"),
      done: getUI("done", "Done"),
      edit: getUI("edit", "Edit"),
      addTag: getUI("add_tag", "Add a tag..."),
      noTags: getUI("no_tags", "No tags yet"),
      noTagsDesc: getUI("no_tags_desc", "Add tags to organize and find this item easily"),
      tagsApplied: getUI("tags_applied", "tags applied"),
    };
  }, [language]);

  const addTagMutation = useAddTag(doctype, name);
  const removeTagMutation = useRemoveTag(doctype, name);

  const debouncedInput = useDebounce(tagInput, 300);
  const { data: suggestions = [], isLoading: isLoadingSuggestions } = useTagSuggestions(
    doctype,
    debouncedInput,
    { enabled: true, isOpen: showSuggestions, minLength: 0 },
  );

  // Filter out tags already applied
  const filteredSuggestions = suggestions.filter((s) => !tags.includes(s)).slice(0, 8);

  const handleAddTag = (tagToAdd?: string) => {
    const tag = tagToAdd || tagInput.trim();
    if (!tag || tags.includes(tag)) return;
    addTagMutation.mutate(tag, {
      onSuccess: () => {
        setTagInput("");
        setShowSuggestions(false);
      },
    });
  };

  const handleRemoveTag = (tag: string) => {
    removeTagMutation.mutate(tag);
  };

  const handleClose = () => {
    setEditMode(false);
    setTagInput("");
    setShowSuggestions(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-h-[85vh] max-w-[680px] p-0 flex flex-col overflow-hidden">
        {/* Top fade gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
            <div>
              <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
                {uiLabels.tags}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {tags.length} {uiLabels.tagsApplied}
              </p>
            </div>
            {tags.length > 0 && (
              <button
                type="button"
                onClick={() => setEditMode((prev) => !prev)}
                className="text-[17px] font-normal text-[#007AFF] hover:text-[#0051D5] active:text-[#0040B3] transition-colors shrink-0"
              >
                {editMode ? uiLabels.done : uiLabels.edit}
              </button>
            )}
          </DialogHeader>

          {/* Tag content */}
          <div className="px-10 pb-8 pt-2">
            {tags.length === 0 ? (
              <div className="flex flex-col items-center py-10">
                <TagIcon className="h-12 w-12 text-muted-foreground/40 mb-3" />
                <p className="text-base font-medium text-foreground">{uiLabels.noTags}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {uiLabels.noTagsDesc}
                </p>
              </div>
            ) : (
              /* Pill cloud inside inset container */
              <div className="bg-gray-50 dark:bg-gray-950 rounded-md border border-gray-200/40 dark:border-gray-700/40 p-5">
                <div className="flex flex-wrap gap-2.5">
                  {tags.map((tag) => (
                    <div
                      key={tag}
                      className="inline-flex items-center gap-1.5 h-10 pl-4 pr-3 rounded-full bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/60 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-200"
                    >
                      <span className="text-[15px] font-medium text-gray-900 dark:text-gray-100">{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={removeTagMutation.isPending}
                        aria-label={`Remove tag ${tag}`}
                        className={cn(
                          "flex items-center justify-center rounded-full transition-all duration-200 disabled:opacity-50",
                          editMode
                            ? "w-6 h-6 opacity-100 text-destructive hover:bg-destructive/10"
                            : "w-0 h-6 opacity-0 overflow-hidden"
                        )}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}

                  {/* Inline input — flows with the pills */}
                  <div className="inline-flex items-center h-10 min-w-[140px] flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder={uiLabels.addTag}
                      value={tagInput}
                      onChange={(e) => {
                        setTagInput(e.target.value);
                        if (!showSuggestions) setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (filteredSuggestions.length > 0 && showSuggestions) {
                            handleAddTag(filteredSuggestions[0]);
                          } else {
                            handleAddTag();
                          }
                        } else if (e.key === "Escape") {
                          setShowSuggestions(false);
                        }
                      }}
                      disabled={addTagMutation.isPending}
                      className="w-full h-full bg-transparent text-[15px] font-normal text-gray-900 dark:text-gray-100 placeholder:text-muted-foreground/50 outline-none disabled:opacity-50"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Suggestion chips — shown when input is focused or has text */}
            {showSuggestions && (filteredSuggestions.length > 0 || isLoadingSuggestions) && (
              <div className="mt-4" ref={suggestionsRef}>
                <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wide mb-2.5">
                  {isLoadingSuggestions ? "Loading..." : "Suggestions"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {!isLoadingSuggestions && filteredSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => handleAddTag(suggestion)}
                      className="h-9 px-3.5 rounded-full border border-dashed border-border/60 text-sm text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/50 transition-colors active:scale-[0.97] touch-manipulation"
                    >
                      + {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Create prompt when typing a new tag */}
            {tagInput.trim().length >= 2 && !tags.includes(tagInput.trim()) && filteredSuggestions.length === 0 && !isLoadingSuggestions && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground/70">
                  Press Enter to create &ldquo;{tagInput.trim()}&rdquo;
                </p>
              </div>
            )}
          </div>

          {/* Bottom fade gradient */}
          <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        </div>

        {/* Sticky footer — outside scroll container */}
        <div className="flex gap-3 px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
          <Button
            className="flex-1 rounded-full h-11 bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white"
            onClick={handleClose}
          >
            {uiLabels.done}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
